import React, {
    useCallback, useMemo, useState,
} from 'react';

import { useDispatch, useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import dynamic from 'next/dynamic';

import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

import { useDevice } from '@bonnet/next/device';

import { useBrand } from '@atc/brand';

import { Chip } from 'reaxl';
import { sendClick } from 'reaxl-analytics';

import { shoppingAssistantCTAClick } from '@/analytics/shoppingAssistantEvents';

import kbbVRSDataDuck from '@/ducks/kbbVRSDataDuck.js';
import paymentsDuck from '@/ducks/paymentsDuck.js';
import srpActiveInteractionDuck from '@/ducks/srp/srpActiveInteractionDuck.js';
import srpFiltersDuck from '@/ducks/srp/srpFiltersDuck.js';

import useSrpNavigation from '@/hooks/useSrpNavigation';

import ShoppingAssistantContainer from '@/containers/ShoppingAssistantContainer';

import LazyComponent from '@/components/LazyComponent';

const CompareTrimsLink = dynamic(() => import(
    /* webpackChunkName: "CompareTrimsLink" */
    '@/components/CompareTrimsLink'
));

const SaveSearchContainer = dynamic(() => import(
    /* webpackChunkName: "SaveSearchContainer" */
    '@/containers/srp/SaveSearchContainer'
    , { ssr: false }
));

const getSelectedMakeModel = (filterValues, modelFilterOptions) => {

    const makeCodeObject = filterValues?.makeCode;
    if (!makeCodeObject || Object.keys(makeCodeObject).length < 1) {
        return null;
    }

    const makeCode = Object.keys(makeCodeObject)[0];
    const makeLabel = makeCodeObject[makeCode];

    const modelCodeKey = `modelCode|${makeCode}`;
    const modelCodeObject = filterValues?.[modelCodeKey];

    if (!modelCodeObject || Object.keys(modelCodeObject).length < 1) {
        return null;
    }

    const modelCode = Object.keys(modelCodeObject)[0];
    const modelLabel = modelCodeObject[modelCode];

    const makeModelLabel = !!(makeLabel && modelLabel) && `${makeLabel} ${modelLabel}`;
    // NOTE: option.value is an array, even if it has only one item
    const selectedModel = Object.values(modelFilterOptions)?.find((option) => option.value?.[0] === modelCode);

    return { makeModelLabel, selectedModel };
};

function SrpShoppingAssistantContainer({ hierarchyData }) {

    const [showAssistant, setShowAssistant] = useState(false);

    const dispatch = useDispatch();
    const navigateToSrp = useSrpNavigation();
    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);

    const { isBrand } = useBrand();
    const isKbb = isBrand('kbb');

    const compareTrims = useSelector(kbbVRSDataDuck.selectors.getCompareTrims);

    // Determine the suggested filter based on hierarchy priority
    const suggestedFilter = useMemo(() => {
        if (hierarchyData.isStrongAttribute) {
            return hierarchyData.firstStrongAttribute;
        }
        if (hierarchyData.isTruckCard) {
            return {
                codes: { bodyStyleSubtypeCode: [] }, // Truck cab size filter
                name: 'Truck Cab Size',
            };
        }
        if (hierarchyData.isSUVCard) {
            return {
                codes: { featureCode: ['1306'] }, // Third row seating feature
                name: 'Third Row Seating',
            };
        }
        if (hierarchyData.isSuggestedFilter) {
            return hierarchyData.firstSuggestedFilter;
        }
        return {};
    }, [hierarchyData]);

    const filterValuesLabels = useSelector(srpFiltersDuck.selectors.getFilterValueLabels);
    const modelFilterOptions = useSelector(srpFiltersDuck.selectors.getModelFilterOptions, _isEqual);

    const {
        makeModelLabel,
        selectedModel,
    } = useMemo(() => getSelectedMakeModel(filterValuesLabels, modelFilterOptions), [filterValuesLabels, modelFilterOptions]) || {};

    const compareTrimTitle = `Compare ${makeModelLabel} Trims`;
    const suggestedFilterTitle = `Search ${suggestedFilter.name || ''} ${makeModelLabel || 'Vehicle'}s`;
    const shopByBudgetTitle = 'Shop by Budget';
    const priceAlertTitle = 'Get Price Alerts';

    const handleAssistantClose = () => setShowAssistant(false);

    const handleSendClick = useCallback((event, input) => {
        sendClick(shoppingAssistantCTAClick, event, {
            input: input || event.rawText,
            isKbb,
        });
    }, [isKbb]);

    const handleCompareTrimsClick = useCallback((event) => {
        handleSendClick(event, compareTrimTitle);

        // Only show the modal if compareTrims data is available
        if (compareTrims && Object.keys(compareTrims).length > 0) {
            // Dispatch after a small delay to ensure container is fully hidden
            setTimeout(() => {
                dispatch(srpActiveInteractionDuck.creators.setKeys({
                    activeModel: selectedModel?.value,
                    showTrimsModal: true,
                }));
            }, 500); // Short delay to improve transition appearance
        }
        handleAssistantClose();
    }, [dispatch, selectedModel, handleSendClick, compareTrims, compareTrimTitle]);

    const handleShopByBudgetClick = useCallback((event) => {
        dispatch(paymentsDuck.creators.updateMyWallet({ displayModal: true }));
        handleSendClick(event, shopByBudgetTitle);
        handleAssistantClose();
    }, [dispatch, handleSendClick]);

    const handleSuggestedFilterClick = useCallback(async (event) => {
        handleSendClick(event, suggestedFilterTitle);

        await navigateToSrp({
            resetPagination: true,
            scroll: !isXs,
            scrollToResultsCount: isXs,
            isNewSearch: true,
            filtersValues: suggestedFilter?.codes,
            action: 'merge',
        });

        handleAssistantClose();
    }, [handleSendClick, suggestedFilterTitle, navigateToSrp, isXs, suggestedFilter]);

    const handlePriceAlertClick = useCallback((event) => {
        handleSendClick(event, priceAlertTitle);
        handleAssistantClose();
    }, [handleSendClick]);

    const compareTrimsCta = (
        <LazyComponent>
            <CompareTrimsLink
                trimFilterOptions={selectedModel}
                className=""
                onClick={handleCompareTrimsClick}
                data-cmp="compare-trims-link"
                uiContext="chip"
            >
                {compareTrimTitle}
            </CompareTrimsLink>
        </LazyComponent>
    );

    const suggestedFilterCta = (
        <Chip
            uiContext="inquiry"
            onClick={handleSuggestedFilterClick}
            data-cmp="suggested-filter-button"
            label={suggestedFilterTitle}
        />
    );

    const shopByBudgetCta = (
        <Chip
            uiContext="inquiry"
            onClick={handleShopByBudgetClick}
            label={shopByBudgetTitle}
        />
    );

    const priceAlertCta = (
        <LazyComponent>
            <SaveSearchContainer
                key="saveSearchContainer"
                isIconCta={false}
                onClickCapture={handlePriceAlertClick}
            >
                {priceAlertTitle}
            </SaveSearchContainer>
        </LazyComponent>
    );

    const suggestedActions = (
        <>
            {selectedModel && compareTrimsCta}
            {suggestedFilter?.codes && suggestedFilterCta}
            {shopByBudgetCta}
            {priceAlertCta}
        </>
    );

    return (
        <ShoppingAssistantContainer
            handleAssistantClose={handleAssistantClose}
            setShowAssistant={setShowAssistant}
            showAssistant={showAssistant}
            suggestedActions={suggestedActions}
        />
    );
}

SrpShoppingAssistantContainer.propTypes = {
    hierarchyData: PropTypes.object.isRequired,
};

export default React.memo(SrpShoppingAssistantContainer, _isEqual);
